(ns app.front.view.inspections.subs
  "Subscriptions related to inspections."
  (:require [app.domain.booking.model :as booking-model]
            [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.view.inspections.booking-utils :as butil]
            [app.front.view.inspections.events :as inspection-events]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [core.front.date-util :as date-util]
            [core.front.feature-flags :as ff]
            [re-frame.core-instrumented :as rf]))

(defn handle-template-kind-entities [entities _]
  (->> entities
       (filter inspection-model/is-inspection-template-kind?)
       (filter du/is-active?)
       (sort-by :ordered/order)))

(rf/reg-sub ::template-kind-entities
  :<- [::ws-subs/entities-for-group]
  handle-template-kind-entities)


(defn handle-selectable-inspection-kinds
  [all _]
  (->> all
       (filter inspection-model/has-templates?)))


(rf/reg-sub ::selectable-inspection-kinds
  :<- [::template-kind-entities]
  handle-selectable-inspection-kinds)


(rf/reg-event-fx
  ::select-my-today-date
  (fn [{:keys [db]} [_ date]]
    {:db         (assoc db ::my-today-date date
                        ::bookings-fetched? false
                        ::external-events-fetched? false)
     :dispatch-n [[::get-bookings date]
                  [::get-external-events date]]}))


(rf/reg-sub ::my-today-date
  #(or (::my-today-date %)
       (date-util/today-start-of-day)))


(rf/reg-sub ::today-selected?
  #(date-util/today? (::my-today-date %)))


(defn same-day?
  "Note that tz (timezone) is only applied to the start-time"
  [selected-date tz start-time]
  (if (every? inst? [selected-date start-time])
    (date-util/same-day? (date-util/to-tz start-time tz)
                         selected-date)
    false))


(defn booked-on-earlier-day?
  "Note that tz (timezone) is only applied to the start-time"
  [selected-date tz start-time]
  (if (every? inst? [selected-date start-time])
    (date-util/before? (date-util/to-tz start-time tz)
                       selected-date)
    false))


;; --- External events ---

(rf/reg-event-fx ::get-external-events
  (fn [_ [_ date]]
    {:comm/command {:command    :user/my-external-events
                    :method     :get
                    :data       {:start (date-util/start-of-day date)
                                 :end   (date-util/end-of-day date)}
                    :on-success [::get-external-events-success]}}))

(defn get-bookings-or-external-events-success [key]
  (fn [{:keys [db]} [_ status changes]]
    {:db       (assoc db key true)
     :dispatch (if (= key ::external-events-fetched?)
                 ;; remove existing ext events, because some may have gotten deleted
                 ;; and we have no clue which
                 [::entities/override-entity-db-for-all {:entity/type :entity.type/external-event} status changes]
                 [::entities/merge-entity-db status changes])}))

(rf/reg-event-fx ::get-external-events-success
  (get-bookings-or-external-events-success ::external-events-fetched?))

(rf/reg-sub ::bookings-and-external-events-fetched?
  #(and (::bookings-fetched? %)
        (::external-events-fetched? %)))

(defn get-timezone-for-external-event [{:keys [:external-event/integration-group]}]
  (or (:group/tz integration-group)
      date-util/browser-timezone))

(defn dates-match-for-external-event? [selected-date {:keys [:external-event/start] :as event}]
  (same-day? selected-date
             (get-timezone-for-external-event event)
             start))

(rf/reg-sub ::my-external-events
  :<- [::entities/context]
  :<- [:session/user]
  (fn [[ctx user] _]
    (entities/find-entities ctx {:entity/type         :entity.type/external-event
                                 :external-event/user (:db/id user)})))

(rf/reg-sub ::my-external-events-for-the-selected-day
  :<- [::my-today-date]
  :<- [::my-external-events]
  (fn [[date events] _]
    (filter #(dates-match-for-external-event? date %)
            events)))

(rf/reg-sub ::my-external-events-for-today
  :<- [::my-external-events]
  (fn [events _]
    (filter #(dates-match-for-external-event? (date-util/today-start-of-day) %)
            events)))

;; --- Bookings ---

(rf/reg-event-fx ::get-bookings
  (fn [_ [_ date]]
    {:comm/command {:command    :booking/for-user
                    :method     :post
                    :data       {:start (date-util/start-of-day date)
                                 :end   (date-util/end-of-day date)}
                    :on-success [::get-bookings-success]}}))

(rf/reg-event-fx ::get-bookings-success
  (get-bookings-or-external-events-success ::bookings-fetched?))

(defn get-timezone-for-inspection [inspection]
  (or (-> inspection
          inspection-model/workspace
          :domain/authority-group
          :group/tz)
      date-util/browser-timezone))

(defn dates-match-for-booking? [selected-date {:keys [:booking/actual-start-time] :as booking}]
  (same-day? selected-date
             (get-timezone-for-inspection (:entity/parent booking))
             actual-start-time))

(defn date-earlier-for-booking? [selected-date {:keys [:booking/actual-start-time] :as booking}]
  (booked-on-earlier-day? selected-date
                          (get-timezone-for-inspection (:entity/parent booking))
                          actual-start-time))

(defn booking-starts-today? [booking]
  (dates-match-for-booking? (date-util/today-start-of-day) booking))

(def visit-of-booking-in-progress?
  (comp (du/in-state-fn :inspection.visit.state/in-progress)
        booking-model/get-visit))

(def visit-of-booking-done?
  (comp (du/in-state-fn :inspection.visit.state/done)
        booking-model/get-visit))

(def inspection-of-booking-completed?
  (comp (du/in-state-fn :inspection.state/completed)
        :entity/parent))

(defn visit-of-booking-done-or-in-progress [booking]
  (or (visit-of-booking-done? booking)
      (visit-of-booking-in-progress? booking)
      (inspection-of-booking-completed? booking)))

(defn get-relevant-bookings [date bookings]
  (->> bookings
       ;; only include those bookings that have not been started
       (remove visit-of-booking-done-or-in-progress)
       (filter #(dates-match-for-booking? date %))))

(defn get-missed-bookings [date bookings]
  (->> bookings
       (filter (du/in-state-fn :booking.state/dispatched))
       ;; only include those bookings that have not been started
       (remove visit-of-booking-done-or-in-progress)
       ;; only include bookings before the selected date AND the current date
       ;; so that looking at a future date doesn't show upcoming bookings as missed
       (filter #(and (date-earlier-for-booking? date %)
                     (date-earlier-for-booking? (date-util/today-start-of-day) %)))))

(rf/reg-sub ::my-bookings
  :<- [::entities/entities {:entity/type :entity.type/booking}]
  :<- [:session/user-id]
  (fn [[bookings user-id] _]
    (filter #(= (:db/id (:booking/assignee %)) user-id)
            bookings)))

(rf/reg-sub ::my-bookings-for-the-selected-day
  :<- [::my-today-date]
  :<- [::my-bookings]
  (fn [[date bookings] _]
    (get-relevant-bookings date bookings)))

(rf/reg-sub ::my-missed-bookings-for-the-selected-day
  :<- [::my-today-date]
  :<- [::my-bookings]
  (fn [[date bookings] _]
    (get-missed-bookings date bookings)))

(rf/reg-sub ::my-bookings-for-today
  :<- [::my-bookings]
  (fn [bookings _]
    (get-relevant-bookings (date-util/today-start-of-day) bookings)))

;; --- Visits ---

(defn dates-match-for-visit? [selected-date {:keys [:inspection.visit/start-time] :as visit}]
  (same-day? selected-date
             (get-timezone-for-inspection (:entity/parent visit))
             start-time))

(defn visit-starts-today? [visit]
  (dates-match-for-visit? (date-util/today-start-of-day) visit))

(defn has-inspector-fn [user-id]
  #(-> % :inspection.visit/inspectors du/entity-id-set (contains? user-id)))

(rf/reg-sub ::finished-visits
  :<- [::entities/entities {:entity/type  :entity.type/inspection-visit
                            :domain/state :inspection.visit.state/done}]
  :<- [:session/user-id]
  (fn [[visits user-id] _]
    (filter (has-inspector-fn user-id)
            visits)))

(rf/reg-sub ::finished-visits-for-the-selected-day
  :<- [::finished-visits]
  :<- [::my-today-date]
  (fn [[visits my-today-date] _]
    (->> visits
         (filter #(dates-match-for-visit? my-today-date %))
         (sort-by :inspection.visit/start-time))))

(rf/reg-sub ::ongoing-visits
  :<- [::entities/entities {:entity/type  :entity.type/inspection-visit
                            :domain/state :inspection.visit.state/in-progress}]
  :<- [:session/user-id]
  (fn [[visits user-id] _]
    (->> visits
         (filter (has-inspector-fn user-id))
         (sort-by :inspection.visit/start-time))))

(rf/reg-sub ::fetching
  (fn [db _]
    (::inspection-events/fetching db #{})))

(rf/reg-sub ::fetching-inspections?
  :<- [::fetching]
  (fn [fetching [_ workspace-id]]
    (contains? fetching workspace-id)))

(rf/reg-sub ::can-have-recurring-inspections?
  (fn [[_ phase-id]]
    [(rf/subscribe [::ws-subs/has-feature? :recurring-inspections?])
     (rf/subscribe [::entities/entity-attr phase-id :entity/type])])
  (fn [[has-feature? phase-entity-type] _]
    (and has-feature? (= :entity.type/execution phase-entity-type))))





(rf/reg-sub ::visit-configuration-enabled?
  :<- [::ff/enabled? :feature-flag.id/inspection-visit-configuration]
  identity)

(rf/reg-sub ::availability
  (fn [[_ auth-group _]]
    (if (group-model/superdomain-calendar? auth-group)
      (rf/subscribe [::entities/entities {:entity/parent (->> auth-group group-model/group-superdomain :db/id)
                                          :entity/type   :entity.type/availability}])
      (rf/subscribe [::entities/entities {:entity/parent (:db/id auth-group)
                                          :entity/type   :entity.type/availability}])))
  (fn [availabilities [_ _ date]]
    (butil/valid-availability date availabilities)))

(rf/reg-sub ::checklist-configuration-v2
  :<- [::ff/enabled? :feature-flag.id/checklist-configuration-v2]
  :<- [::ff/enabled? :feature-flag.id/checklist-configuration-v2-mobile]
  :<- [::ws-subs/has-feature? :group.feature/mobile-app]
  group-model/has-mobile-feature?)

(rf/reg-sub ::inspections-tr
  :<- [:tr]
  :<- [:session/code-enforcement-authority?]
  :<- [:session/work-orders-authority?]
  (fn [[tr ce-auth? wo-auth?] _]
    (i18n-model/wrap-tr-fn
      (i18n-model/code-enforcement-work-order-replacements ce-auth? wo-auth?)
      tr)))
